import { useState, useEffect, useRef } from 'react';
import {
  getActionPlanTasks,
  getActionPlanSummary,
  updateTaskStatus,
  generateActionPlan,
} from '../../../services/actionPlanService';
import type { ActionPlanTask, ActionPlanSummary } from '../../../services/actionPlanService';
import { generateActionPlanPdf } from '../../../services/actionPlanPdfService';
import { apiService } from '../../../services/api';
import { SEO_AS_IS_TABS } from '../../../constants/telemetryModules';
import { usePageTracking } from '../../../hooks/usePageTracking';
import { useTelemetry } from '../../../hooks/useTelemetry';
import { MODULES } from '../../../telemetry/constants/modules';
import { ACTIONS } from '../../../telemetry/constants/actions';
import TaskDetailsModal from './TaskDetailsModal';

interface ActionPlanProps {
  onTooltipShow: (title: string, content: string) => void;
}

const formatGoalLabel = (goal?: string) => {
  if (!goal) return '';
  const goalMap: Record<string, string> = {
    'domain-authority': 'Improve Domain Authority',
    'organic-traffic': 'Increase Organic Traffic',
    'keyword-rankings': 'Improve Keyword Rankings',
    'serp-features': 'Captured Visibility',
    'avg-position': 'Reduce Average Position',
    'impressions': 'Increase Impressions',
  };
  return goal
    .split(',')
    .map((g) => {
      const trimmed = g.trim();
      return goalMap[trimmed] || trimmed.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
    })
    .join(', ');
};

export default function ActionPlan({ onTooltipShow }: ActionPlanProps) {
  const [activeTab, setActiveTab] = useState<'onpage' | 'offpage' | 'technical'>('onpage');

  // Sync activeTab with URL hash on mount / hash change
  useEffect(() => {
    const hash = window.location.hash.toLowerCase();
    if (hash.includes('offpage')) {
      setActiveTab('offpage');
    } else if (hash.includes('technical') || hash.includes('cwv')) {
      setActiveTab('technical');
    } else if (hash.includes('onpage')) {
      setActiveTab('onpage');
    }
  }, []);

  usePageTracking({
    moduleName: 'SEO Manager',
    submoduleName: `Action Plan / ${SEO_AS_IS_TABS[activeTab]}`,
    routePath: `/dashboard#action-plan/${activeTab}`,
  });
  const { trackAction } = useTelemetry({ module: MODULES.SEO_MANAGER, submodule: 'Action Plan' });
  const [filterPriority, setFilterPriority] = useState('all');
  const [tasks, setTasks] = useState<ActionPlanTask[]>([]);
  const [summary, setSummary] = useState<ActionPlanSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTask, setSelectedTask] = useState<ActionPlanTask | null>(null);
  const [websiteUrl, setWebsiteUrl] = useState<string>('');
  const [brandLogoUrl, setBrandLogoUrl] = useState<string>('');
  const [businessName, setBusinessName] = useState<string>('');
  const [downloadingCategory, setDownloadingCategory] = useState<'all' | 'onpage' | 'offpage' | 'technical' | null>(null);
  const [downloadProgressText, setDownloadProgressText] = useState<string | null>(null);
  const isGeneratingRef = useRef(false);

  const userId = localStorage.getItem('user_id');

  useEffect(() => {
    if (!userId) {
      window.location.href = '/login';
    }
  }, [userId]);

  // Fetch action plan data on mount
  useEffect(() => {
    if (!userId) return;
    fetchActionPlanData();

    // Fetch user website URL & brand logo for PDF export
    const fetchWebsiteUrl = async () => {
      try {
        const res = await apiService.getUserProfile(userId);
        const data = res?.data || res;
        if (data?.website_url) {
          setWebsiteUrl(data.website_url);
        }
        if (data?.logo_url) {
          setBrandLogoUrl(data.logo_url);
        }
        if (data?.business_name) {
          setBusinessName(data.business_name);
        }
      } catch (err) {
        console.warn('Could not fetch profile details for PDF export:', err);
      }
    };
    fetchWebsiteUrl();
  }, [userId]);

  // Deduplicate tasks by category + parameter_group + sub_parameter
  const deduplicateTasks = (rawTasks: ActionPlanTask[]): ActionPlanTask[] => {
    const map = new Map<string, ActionPlanTask>();
    for (const t of rawTasks) {
      const key = `${t.category}_${t.parameter_group}_${t.sub_parameter || t.title}`;
      if (!map.has(key)) {
        map.set(key, t);
      }
    }
    return Array.from(map.values());
  };

  const handleDownloadPdf = async (cat: 'all' | 'onpage' | 'offpage' | 'technical') => {
    if (downloadingCategory) return;
    try {
      setDownloadingCategory(cat);
      setDownloadProgressText('Initializing PDF...');
      trackAction(ACTIONS.DOWNLOAD, `Download Action Plan PDF (${cat})`, { category: cat });
      await generateActionPlanPdf({
        tasks,
        category: cat,
        userId,
        websiteUrl,
        brandLogoUrl,
        businessName,
        onProgress: (msg) => setDownloadProgressText(msg),
      });
    } catch (err) {
      console.error('Failed to generate PDF:', err);
      alert(err instanceof Error ? err.message : 'Failed to generate PDF');
    } finally {
      setDownloadingCategory(null);
      setDownloadProgressText(null);
    }
  };

  const fetchActionPlanData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Try to fetch existing tasks
      const allTasks = await getActionPlanTasks(userId);

      // If no tasks exist, generate them then poll until tasks appear
      if (allTasks.length === 0) {
        if (isGeneratingRef.current) return;
        isGeneratingRef.current = true;
        try {
          console.log('No tasks found, generating action plan...');
          trackAction(ACTIONS.GENERATE_ACTION_PLAN, 'Generating Action Plan', { user_id: userId });
          
          // Trigger generation (may return early or run async on backend)
          generateActionPlan(userId).catch(err => {
            console.warn('Action plan trigger warning/timeout:', err);
          });

          // Poll for tasks — the backend makes LLM calls per task so it may take up to 20 minutes
          let newTasks: ActionPlanTask[] = [];
          const MAX_RETRIES = 240; // 240 * 5s = 1200s (20 minutes)
          const RETRY_DELAY_MS = 5000;
          for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
            await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));
            newTasks = await getActionPlanTasks(userId);
            if (newTasks.length > 0) {
              console.log(`Tasks successfully ready after attempt ${attempt + 1}`);
              break;
            }
            console.log(`Tasks not ready yet, polling attempt ${attempt + 1}/${MAX_RETRIES}...`);
          }
          setTasks(deduplicateTasks(newTasks));
        } finally {
          isGeneratingRef.current = false;
        }
      } else {
        setTasks(deduplicateTasks(allTasks));
      }

      // Fetch summary
      const summaryData = await getActionPlanSummary(userId);
      setSummary(summaryData);

    } catch (err) {
      console.error('Error fetching action plan:', err);
      setError(err instanceof Error ? err.message : 'Failed to load action plan');
    } finally {
      setLoading(false);
    }
  };

  // Filter tasks by active tab and priority
  const filteredTasks = tasks
    .filter(task => task.category === activeTab)
    .filter(task => filterPriority === 'all' || task.priority === filterPriority);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'text-red-600 bg-red-100 border-red-200';
      case 'medium':
        return 'text-yellow-600 bg-yellow-100 border-yellow-200';
      case 'low':
        return 'text-green-600 bg-green-100 border-green-200';
      default:
        return 'text-gray-600 bg-gray-100 border-gray-200';
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'ri-star-fill';
      case 'medium':
        return 'ri-star-half-fill';
      case 'low':
        return 'ri-star-line';
      default:
        return 'ri-star-line';
    }
  };

  const getEffortColor = (effort?: string) => {
    switch (effort) {
      case 'Low':
        return 'text-green-600';
      case 'Medium':
        return 'text-yellow-600';
      case 'High':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const handleMarkAsComplete = async (taskId: number) => {
    if (!userId) return;
    try {
      trackAction(ACTIONS.COMPLETE_TASK, 'Task Completed', { task_id: taskId });
      await updateTaskStatus(taskId, userId, 'completed');
      // Refresh data
      await fetchActionPlanData();
    } catch (err) {
      console.error('Error updating task status:', err);
      alert('Failed to update task status');
    }
  };

  if (!userId) {
    return null;
  }

  if (loading || isGeneratingRef.current) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            {isGeneratingRef.current ? 'Generating AI Action Plan...' : 'Loading action plan...'}
          </h3>
          {isGeneratingRef.current && (
            <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto text-sm">
              Our AI is analyzing your website's AS-IS state and drafting customized, actionable tasks. 
              This usually takes 1-2 minutes.
            </p>
          )}
        </div>
      </div>
    );
  }

  if (error) {
    // FIX 6: Universe not yet finalized — show setup prompt instead of generic error
    const isSetupRequired =
      error.includes('Universe not yet finalized') ||
      error.includes('AS-IS State setup') ||
      error.includes('not yet complete');

    if (isSetupRequired) {
      return (
        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-6 border border-amber-200 dark:border-amber-800">
          <div className="flex items-start space-x-3">
            <i className="ri-lock-line text-2xl text-amber-600"></i>
            <div>
              <h3 className="font-semibold text-amber-900 dark:text-amber-200">Action Plan Locked</h3>
              <p className="text-amber-700 dark:text-amber-300 mt-1">
                Your AS-IS analysis is not yet complete. Finish setup to unlock your Action Plan.
              </p>
              <p className="text-amber-600 dark:text-amber-400 mt-2 text-sm">
                Complete the AS-IS State analysis and finalize your keyword universe to generate your personalised action plan.
              </p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 border border-red-200 dark:border-red-800">
        <div className="flex items-start space-x-3">
          <i className="ri-error-warning-line text-2xl text-red-600"></i>
          <div>
            <h3 className="font-semibold text-red-900 dark:text-red-200">Error Loading Action Plan</h3>
            <p className="text-red-700 dark:text-red-300 mt-1">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Get current tab tasks
  const currentTasks = filteredTasks;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center space-x-2">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Action Plan</h2>
              <button
                onClick={() => onTooltipShow('Action Plan', 'Actionable tasks to improve your As-Is state and achieve your goals. These tasks are prioritized based on impact and effort, helping you focus on what matters most for your business.')}
                className="text-gray-400 hover:text-blue-500"
              >
                <i className="ri-information-line text-xl"></i>
              </button>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mt-1 text-sm">Actionable tasks to improve As-Is state and achieve goals</p>
          </div>

          {/* Top "Download Full Action Plan (PDF)" Button */}
          <button
            onClick={() => handleDownloadPdf('all')}
            disabled={loading || tasks.length === 0 || !!downloadingCategory}
            className="flex items-center space-x-2 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm self-start sm:self-auto cursor-pointer"
            title="Download complete action plan (On-Page, Off-Page, Core Web Vitals) with View Details as a PDF"
          >
            {downloadingCategory === 'all' ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span className="text-xs sm:text-sm">{downloadProgressText || 'Generating Full PDF...'}</span>
              </>
            ) : (
              <>
                <i className="ri-download-cloud-2-line text-lg"></i>
                <span className="whitespace-nowrap">Download Full Action Plan (PDF)</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-lg border border-gray-200 dark:border-gray-700">
        <div className="flex space-x-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
          {[
            { id: 'onpage' as const, label: 'On-Page', icon: 'ri-file-text-line', count: tasks.filter(t => t.category === 'onpage').length },
            { id: 'offpage' as const, label: 'Off-Page', icon: 'ri-links-line', count: tasks.filter(t => t.category === 'offpage').length },
            { id: 'technical' as const, label: 'Core Web Vitals', icon: 'ri-speed-line', count: tasks.filter(t => t.category === 'technical').length }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-lg transition-all duration-200 ${activeTab === tab.id
                  ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
            >
              <i className={`${tab.icon} text-lg`}></i>
              <span className="font-medium whitespace-nowrap">{tab.label}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${activeTab === tab.id
                  ? 'bg-blue-100 text-blue-600'
                  : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-400'
                }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Priority Filter + Category PDF Download Button */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-lg border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center space-x-3 flex-wrap gap-y-2">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white whitespace-nowrap">Filter by Priority:</h3>
            <div className="flex flex-wrap gap-2">
              {[
                { id: 'all', label: 'All Tasks', icon: 'ri-list-check' },
                { id: 'high', label: 'High Priority', icon: 'ri-star-fill' },
                { id: 'medium', label: 'Medium', icon: 'ri-star-half-fill' },
                { id: 'low', label: 'Low', icon: 'ri-star-line' }
              ].map((filter) => (
                <button
                  key={filter.id}
                  onClick={() => setFilterPriority(filter.id)}
                  className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 ${filterPriority === filter.id
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                >
                  <i className={`${filter.icon} text-xs`}></i>
                  <span>{filter.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Category Download Button */}
          <button
            onClick={() => handleDownloadPdf(activeTab)}
            disabled={loading || tasks.filter(t => t.category === activeTab).length === 0 || !!downloadingCategory}
            className="flex items-center space-x-2 px-3.5 py-2 bg-blue-50 hover:bg-blue-100 dark:bg-gray-700 dark:hover:bg-gray-600 border border-blue-200 dark:border-gray-600 text-blue-700 dark:text-blue-300 rounded-lg hover:border-blue-300 transition-all text-xs font-semibold whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed shadow-sm self-start sm:self-auto cursor-pointer"
            title={`Download only ${activeTab === 'onpage' ? 'On-Page' : activeTab === 'offpage' ? 'Off-Page' : 'Core Web Vitals'} action plan as PDF`}
          >
            {downloadingCategory === activeTab ? (
              <>
                <div className="w-3.5 h-3.5 border-2 border-blue-600 dark:border-blue-400 border-t-transparent rounded-full animate-spin"></div>
                <span>{downloadProgressText || 'Generating PDF...'}</span>
              </>
            ) : (
              <>
                <i className="ri-file-pdf-line text-sm text-red-500 dark:text-red-400"></i>
                <span>
                  Download {activeTab === 'onpage' ? 'On-Page' : activeTab === 'offpage' ? 'Off-Page' : 'Core Web Vitals'} PDF
                </span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Tasks List */}
      <div className="space-y-4">
        {currentTasks.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl p-12 shadow-lg border border-gray-200 dark:border-gray-700 text-center">
            <i className="ri-task-line text-6xl text-gray-300 dark:text-gray-600 mb-4"></i>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No Tasks Found</h3>
            <p className="text-gray-600 dark:text-gray-400">
              {filterPriority !== 'all'
                ? `No ${filterPriority} priority tasks in this category.`
                : 'No tasks available in this category.'}
            </p>
          </div>
        ) : (
          currentTasks.map((task) => (
            <div
              key={task.id}
              className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start space-x-4 flex-1">
                  {/* Priority Star */}
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center border ${getPriorityColor(task.priority)}`}>
                    <i className={`${getPriorityIcon(task.priority)} text-xl`}></i>
                  </div>

                  {/* Task Details */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white">{task.title}</h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium border whitespace-nowrap ml-4 ${getPriorityColor(task.priority)}`}>
                        {task.priority.toUpperCase()}
                      </span>
                    </div>

                    <div className="space-y-2">
                      {task.parameter_group && (
                        <div className="flex items-center space-x-2 text-sm">
                          <i className="ri-folder-line text-gray-400"></i>
                          <span className="text-gray-600 dark:text-gray-400">{task.parameter_group}</span>
                        </div>
                      )}

                      {task.impact_description && (
                        <div className="flex items-center space-x-2 text-sm">
                          <i className="ri-lightbulb-line text-yellow-500"></i>
                          <span className="text-gray-700 dark:text-gray-300">{task.impact_description}</span>
                        </div>
                      )}

                      {/* AI Recommendation: show recommendation if available, else fall back to description */}
                      {(task.recommendation || task.description) && (
                        <div className="flex items-start space-x-2 text-sm mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
                          <i className="ri-robot-line text-blue-500 mt-0.5 flex-shrink-0"></i>
                          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                            {task.recommendation ?? task.description}
                          </p>
                        </div>
                      )}

                      <div className="flex items-center space-x-4 text-sm">
                        {task.effort_description && (
                          <div className="flex items-center space-x-2">
                            <i className="ri-time-line text-gray-400"></i>
                            <span className="text-gray-600 dark:text-gray-400">Effort:</span>
                            <span className={`font-semibold ${getEffortColor(task.effort_description)}`}>{task.effort_description}</span>
                          </div>
                        )}

                        {task.related_goal && (
                          <div className="flex items-start space-x-2">
                            <i className="ri-flag-line text-blue-500 mt-0.5 flex-shrink-0"></i>
                            <span className="text-gray-600 dark:text-gray-400 flex-shrink-0">Goal:</span>
                            <span className="font-semibold text-blue-600 leading-snug">{formatGoalLabel(task.related_goal)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Button */}
              <div className="flex items-center justify-end pt-4 border-t border-gray-200 dark:border-gray-700 space-x-3">
                <button
                  onClick={() => setSelectedTask(task)}
                  className="flex items-center space-x-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  <i className="ri-eye-line text-lg"></i>
                  <span className="text-sm font-medium whitespace-nowrap">View Details</span>
                </button>
                {task.status === 'completed' ? (
                  <div className="flex items-center space-x-2 text-green-600 px-4 py-2">
                    <i className="ri-check-double-line text-xl"></i>
                    <span className="font-medium">Completed</span>
                  </div>
                ) : (
                  <button
                    onClick={() => handleMarkAsComplete(task.id)}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <span className="text-sm font-medium whitespace-nowrap">Mark as Complete</span>
                    <i className="ri-check-line"></i>
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Summary Card */}
      {summary && (
        <div className="bg-gradient-to-r from-green-500 to-teal-600 rounded-xl p-6 shadow-lg">
          <div className="flex items-center justify-between text-white">
            <div>
              <h3 className="text-xl font-bold mb-2">Your Action Plan Summary</h3>
              <p className="text-green-100">
                {currentTasks.length} tasks in {activeTab === 'onpage' ? 'On-Page' : activeTab === 'offpage' ? 'Off-Page' : 'Core Web Vitals'} category
              </p>
            </div>
            <div className="text-right">
              <div className="flex items-center space-x-4">
                <div>
                  <div className="text-2xl font-bold">{summary.completed}</div>
                  <div className="text-xs text-green-100">Completed</div>
                </div>
                <div>
                  <div className="text-2xl font-bold">{summary.in_progress}</div>
                  <div className="text-xs text-green-100">In Progress</div>
                </div>
                <div>
                  <div className="text-2xl font-bold">{summary.not_started}</div>
                  <div className="text-xs text-green-100">Not Started</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      <TaskDetailsModal
        isOpen={!!selectedTask}
        onClose={() => setSelectedTask(null)}
        task={selectedTask}
      />
    </div>
  );
}
